"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTheme } from '../../../contexts/ThemeProvider';

// Интерфейс для состояния персонажа
export interface CharacterState {
  HighKick: number;
  punch: number;
  block: number;
  evasion: number;
  LowKick: number;
  legDamage: number;
  LowKickDamage: number;
  punchDamage: number;
  blockPower: number;
  health: number;
  currentHealth: number;
  damageBonus: number;
}

// Интерфейс для контекста
interface CharacterContextType {
  character: CharacterState;
  updateCharacter: (stat: string, value: number) => void;
  calculateStats: (weights: CharacterState) => CharacterState;
  getActionColor: (action: string) => string;
  getInitial: (name: string) => string;
  isDark: boolean;
}

// Создаем контекст с начальным значением
const CharacterContext = createContext<CharacterContextType | undefined>(undefined);

// Начальное состояние персонажа
const initialCharacterState: CharacterState = {
  HighKick: 10,
  punch: 10,
  block: 10,
  evasion: 10,
  LowKick: 10,
  legDamage: 0,
  LowKickDamage: 0,
  punchDamage: 0,
  blockPower: 0,
  health: 1000,
  currentHealth: 1000,
  damageBonus: 0
};

// Провайдер контекста
export const CharacterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [character, setCharacter] = useState<CharacterState>(initialCharacterState);
  const { isDark } = useTheme(); // Используем тему из централизованного провайдера
  
  // Загрузка сохраненного состояния при инициализации
  useEffect(() => {
    const savedCharacter = localStorage.getItem('character');
    if (savedCharacter) {
      try {
        const parsed = JSON.parse(savedCharacter);
        // Пересчитываем статистики для уверенности
        setCharacter(calculateStats(parsed));
      } catch (e) {
        console.error('Ошибка при загрузке сохраненного персонажа:', e);
        // Используем начальное состояние при ошибке
        setCharacter(calculateStats(initialCharacterState));
      }
    } else {
      // Нет сохраненных данных, используем начальное состояние
      setCharacter(calculateStats(initialCharacterState));
    }
  }, []);
  
  // Сохраняем состояние в localStorage при его изменении
  useEffect(() => {
    localStorage.setItem('character', JSON.stringify(character));
  }, [character]);
  
  // Получение цвета для конкретного действия
  const getActionColor = (action: string): string => {
    const actionColors: {[key: string]: string} = {
      'Удар рукой': 'bg-red-600', // красный
      'Удар ногой': 'bg-orange-600', // оранжевый
      'Удар ногой в корпус': 'bg-amber-600', // янтарный
      'Блок': 'bg-blue-600', // синий
      'Уклонение': 'bg-green-600', // зеленый
      'Здоровье': 'bg-purple-500', // фиолетовый
    };
    
    return actionColors[action] || 'bg-gray-500';
  };

  // Получение инициала для конкретного действия
  const getInitial = (name: string): string => {
    switch(name) {
      case 'Удар рукой': return 'P';
      case 'Удар ногой': return 'H';
      case 'Удар ногой в корпус': return 'K';
      case 'Блок': return 'B';
      case 'Уклонение': return 'E';
      case 'Здоровье': return 'HP';
      default: return name.charAt(0).toUpperCase();
    }
  };
  
  // Функция для расчета статистики на основе весов
  const calculateStats = (weights: CharacterState): CharacterState => {
    console.log('calculateStats вызван с весами:', weights);

    // Делаем копию входящих весов, чтобы избежать мутаций
    const weightsCopy = { ...weights };

    // Расчет базового урона HighKick
    let legDamage = Math.round(150 + ((weightsCopy.HighKick - 1) * (250 - 150) / (39 - 1)));
    
    // Добавляем бонус урона от высокого уклонения
    if (weightsCopy.evasion > 20) {
      legDamage += (weightsCopy.evasion - 20) * 5;
    }
    
    // Расчет урона LowKick
    let LowKickDamage = Math.round(150 + ((weightsCopy.LowKick - 1) * (300 - 150) / (39 - 1)));
    // Добавляем бонус урона от высокого уклонения
    if (weightsCopy.evasion > 20) {
      LowKickDamage += (weightsCopy.evasion - 20) * 4;
    }
    
    // Расчет урона удара кулаком
    let punchDamage = Math.round(100 + ((weightsCopy.punch - 1) * (200 - 100) / (39 - 1)));
    console.log('Расчет punchDamage:', {
      punch: weightsCopy.punch,
      базовыйPunchDamage: punchDamage
    });
    
    // Добавляем бонус урона, если оба удара ногой 20 или выше
    if (weightsCopy.HighKick >= 20 && weightsCopy.LowKick >= 20) {
      punchDamage += weightsCopy.punch * 6;
      console.log('Добавлен бонус за удары ногами:', weightsCopy.punch * 6);
    }
    
    // Расчет силы блока - многоуровневая система
    let blockPower = 0;
    if (weightsCopy.block <= 10) {
      blockPower = weightsCopy.block * 7;
    } else if (weightsCopy.block <= 20) {
      blockPower = (10 * 7) + ((weightsCopy.block - 10) * 5);
    } else {
      blockPower = (10 * 7) + (10 * 5) + ((weightsCopy.block - 20) * 3);
    }
    
    // Применяем 2% снижения урона за каждый пункт, если блок 20 или выше
    const damageReduction = weightsCopy.block >= 20 ? Math.pow(0.98, weightsCopy.block - 20) : 1;
    
    // Применяем снижение урона ко всем типам атак
    legDamage = Math.round(legDamage * damageReduction);
    LowKickDamage = Math.round(LowKickDamage * damageReduction);
    punchDamage = Math.round(punchDamage * damageReduction);
    
    // Рассчитываем процент снижения урона для отображения
    const damageBonus = weightsCopy.block >= 20 ? Math.round((1 - damageReduction) * 100) : 0;
    
    // Начинаем с базового здоровья 1000
    const baseHealth = 1000;
    // Добавляем бонус здоровья от блока (25 за пункт)
    const blockBonus = weightsCopy.block * 25;
    // Применяем штраф от уклонения для значений выше 10
    const evasionPenalty = weightsCopy.evasion > 10 ? (weightsCopy.evasion - 10) * 10 : 0;
    const health = Math.round(Math.max(0, baseHealth + blockBonus - evasionPenalty));
  
    const result = {
      ...weightsCopy,
      legDamage,
      LowKickDamage,
      punchDamage,
      blockPower,
      health,
      currentHealth: health,
      damageBonus
    };

    console.log('Результат calculateStats:', result);
    return result;
  };
  
  // Обновление характеристик персонажа
  const updateCharacter = (stat: string, value: number) => {
    console.log(`updateCharacter вызван: ${stat} = ${value}`);
    
    setCharacter(prevState => {
      // Проверяем, изменилось ли значение
      if (prevState[stat as keyof CharacterState] === value) {
        console.log('Значение не изменилось, возвращаем предыдущее состояние');
        return prevState;
      }
      
      // Создаем новый объект весов с обновленным статом
      const newWeights = { ...prevState, [stat]: value };
      console.log('Новые веса:', newWeights);
      
      // Рассчитываем общий вес для статов атаки и защиты
      const totalWeight = Object.entries(newWeights)
        .filter(([key]) => ['HighKick', 'punch', 'block', 'evasion', 'LowKick'].includes(key))
        .reduce((sum, [, value]) => sum + (value as number), 0);
      
      console.log('Общий вес:', totalWeight);
      
      // Проверяем, не превышает ли общий вес 100
      if (totalWeight > 100) {
        console.log('Общий вес превышает 100, оставляем предыдущее состояние');
        return prevState; // Возвращаем предыдущее состояние без изменений
      }
      
      // Рассчитываем новую статистику на основе обновленных весов
      const calculatedStats = calculateStats(newWeights);
      console.log('Рассчитанные статистики:', calculatedStats);
      return calculatedStats;
    });
  };

  return (
    <CharacterContext.Provider 
      value={{ 
        character, 
        updateCharacter, 
        calculateStats,
        getActionColor,
        getInitial,
        isDark
      }}
    >
      {children}
    </CharacterContext.Provider>
  );
};

// Хук для использования контекста персонажа
export const useCharacter = (): CharacterContextType => {
  const context = useContext(CharacterContext);
  if (context === undefined) {
    throw new Error('useCharacter must be used within a CharacterProvider');
  }
  return context;
};

export default CharacterProvider; 